import type { DisruptionPaxRosterRow } from '@/lib/disruptions/types';

// WHO sleeps in WHICH room, for the PNR card's reservation.
//
// Booking models one reservation per PNR with a room COUNT — never which
// passenger goes where (the rooming list says the same: the physical split stays
// with the hotel). The card still has to answer that at a glance, so this lays the
// PNR's people over the rooms with the rule the count was sized from: ~2
// bed-taking passengers per room (`booking:capacity.ledger#roomsNeeded`), lap
// infants take no bed and ride with their adult. Adults are dealt first, one per
// room, so a minor is only alone when the reservation leaves no other choice.

export type RoomOccupantKind = 'ADULT' | 'MINOR' | 'INFANT';

export interface RoomOccupant {
  paxUrn: string;
  /** Masked like every roster name. */
  name: string;
  kind: RoomOccupantKind;
}

export interface RoomSplit {
  /** 1-based. */
  room: number;
  occupants: RoomOccupant[];
}

type SplitPax = Pick<
  DisruptionPaxRosterRow,
  'paxUrn' | 'fullName' | 'paxType' | 'condition' | 'status' | 'responsibleAdult'
>;

/** Mirrors the rooming list's `conditionOf` (operator-bff documents.shared). */
export function occupantKind(p: Pick<SplitPax, 'paxType' | 'condition'>): RoomOccupantKind {
  const type = (p.paxType ?? '').toUpperCase();
  if (type === 'INF' || type === 'INFANT') return 'INFANT';
  if (p.condition === 'ACCOMPANIED_MINOR' || p.condition === 'UNACCOMPANIED_MINOR') return 'MINOR';
  if (p.condition === 'ADULT') return 'ADULT';
  return type === 'CHD' || type === 'CHILD' ? 'MINOR' : 'ADULT';
}

function staying(passengers: readonly SplitPax[]): SplitPax[] {
  return passengers.filter((p) => (p.status ?? '').toUpperCase() !== 'EXCLUDED');
}

/** Guests that take a bed — everyone staying but lap infants. */
export function bedGuests(passengers: readonly SplitPax[]): number {
  return staying(passengers).filter((p) => occupantKind(p) !== 'INFANT').length;
}

/**
 * Lay the PNR's passengers over `rooms` rooms (the reservation's count; absent →
 * the ~2-per-room rule booking sizes with). Pure and deterministic: roster order
 * decides ties, so the card never reshuffles between renders.
 */
export function splitIntoRooms(passengers: readonly SplitPax[], rooms?: number): RoomSplit[] {
  const people = staying(passengers).map((p) => ({
    paxUrn: p.paxUrn,
    name: p.fullName,
    kind: occupantKind(p),
    adultUrn: p.responsibleAdult?.paxUrn,
  }));
  const adults = people.filter((p) => p.kind === 'ADULT');
  const minors = people.filter((p) => p.kind === 'MINOR');
  const infants = people.filter((p) => p.kind === 'INFANT');
  const beds = adults.length + minors.length;
  const count = rooms !== undefined && rooms > 0 ? Math.floor(rooms) : Math.max(1, Math.ceil(beds / 2));
  const capacity = Math.max(1, Math.ceil(beds / count));

  const out: RoomSplit[] = Array.from({ length: count }, (_, i) => ({ room: i + 1, occupants: [] }));
  const roomOf = new Map<string, RoomSplit>();
  const place = (room: RoomSplit, p: (typeof people)[number]): void => {
    room.occupants.push({ paxUrn: p.paxUrn, name: p.name, kind: p.kind });
    roomOf.set(p.paxUrn, room);
  };
  const bedsIn = (r: RoomSplit): number => r.occupants.filter((o) => o.kind !== 'INFANT').length;
  const hasAdult = (r: RoomSplit): boolean => r.occupants.some((o) => o.kind === 'ADULT');
  const emptiest = (pool: RoomSplit[]): RoomSplit | undefined =>
    pool.reduce<RoomSplit | undefined>((best, r) => (!best || bedsIn(r) < bedsIn(best) ? r : best), undefined);

  adults.forEach((a, i) => place(out[i % count]!, a));
  for (const m of minors) {
    const own = m.adultUrn ? roomOf.get(m.adultUrn) : undefined;
    const withRoom = (r: RoomSplit): boolean => bedsIn(r) < capacity;
    const target =
      (own && withRoom(own) ? own : undefined) ??
      emptiest(out.filter((r) => hasAdult(r) && withRoom(r))) ??
      emptiest(out)!;
    place(target, m);
  }
  for (const inf of infants) {
    const own = inf.adultUrn ? roomOf.get(inf.adultUrn) : undefined;
    const infantsIn = (r: RoomSplit): number => r.occupants.filter((o) => o.kind === 'INFANT').length;
    const withAdult = out.filter(hasAdult);
    const target =
      own ??
      (withAdult.length
        ? withAdult.reduce((best, r) => (infantsIn(r) < infantsIn(best) ? r : best))
        : out[0]!);
    place(target, inf);
  }
  return out;
}
